import React from 'react';
import { Box, Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ESG_ICONS } from '../../constants/esgIcons';
import { useNumberFormat } from '../../hooks/useNumberFormat';
import type { OrganizationCarbonAvoidanceReport } from '../../types/carbon-avoidance-report.type';
import type { EsgCarbonWidgetId } from '../../types/dashboard-customize.type';
import { mapReportStreamsToChartRows } from '../../utils/carbonAvoidance.util';
import KpiFigure from '../metrics/KpiFigure';
import MetricStrip from '../metrics/MetricStrip';
import CarbonAvoidanceByStreamTable from '../report/CarbonAvoidanceByStreamTable';
import CarbonAvoidedByMaterialTable from './CarbonAvoidedByMaterialTable';
import CarbonAvoidedByStreamChart from './CarbonAvoidedByStreamChart';
import EsgSection from './sections/EsgSection';
import PerformanceHero from './sections/PerformanceHero';

interface CarbonAvoidanceDetailViewProps {
  report: OrganizationCarbonAvoidanceReport;
  periodLabel: string;
  visibleWidgets?: readonly EsgCarbonWidgetId[];
}

/**
 * Carbon Avoidance tab: avoided emissions for a custom period, reported
 * outside the GHG inventory, with the breakdown by material behind it.
 * Methodology lives in the Knowledge Base ("Methodology & data quality").
 */
const CarbonAvoidanceDetailView: React.FC<CarbonAvoidanceDetailViewProps> = ({
  report,
  periodLabel,
  visibleWidgets,
}) => {
  const { t } = useTranslation();
  const fmt = useNumberFormat();
  const { executive_summary: summary } = report;
  const skipped = summary.skipped_run_count ?? 0;
  const failed = summary.failed_run_count ?? 0;
  const excluded = skipped + failed;
  const chartStreams = mapReportStreamsToChartRows(report.by_stream);

  const show = (id: EsgCarbonWidgetId) => !visibleWidgets || visibleWidgets.includes(id);
  const showStrip = show('weight_diverted') || show('completed_runs') || show('skipped_runs');
  const showBreakdown = show('stream_chart') || show('stream_table') || show('material_table');

  const total = fmt.metric('tco2e', summary.total_avoided_tco2e ?? 0);

  if (!show('total_avoided') && !showStrip && !showBreakdown) return null;

  const periodChip = (
    <Chip
      size="small"
      variant="outlined"
      icon={<ESG_ICONS.period sx={{ fontSize: 14 }} />}
      label={periodLabel}
      sx={{ color: 'text.secondary' }}
    />
  );

  return (
    <Box>
      {(show('total_avoided') || showStrip) && (
        <EsgSection
          title={t('dashboard.sections.avoidedEmissions')}
          icon={ESG_ICONS.avoidedEmissions}
          description={t('dashboard.sectionDescriptions.avoidedEmissions')}
          meta={periodChip}
        >
          <Box sx={{ display: 'grid', gap: { xs: 2, md: 2.5 } }}>
            {show('total_avoided') && (
              <PerformanceHero
                lead={{
                  label: t('carbonAvoidance.totalAvoided', { period: periodLabel }),
                  icon: ESG_ICONS.avoidedEmissions,
                  value: total.text,
                  unit: total.unit,
                  context: t('dashboard.basedOnDeliveryDate'),
                }}
              />
            )}
            {showStrip && (
              <MetricStrip columns={{ xs: 2, sm: 2, md: 4 }}>
                {show('weight_diverted') && (
                  <KpiFigure
                    label={t('carbonAvoidance.weightDiverted')}
                    icon={ESG_ICONS.mass}
                    value={fmt.metric('mass_t', summary.total_weight_diverted_tonnes).text}
                    unit={t('units.tonnes')}
                  />
                )}
                <KpiFigure
                  label={t('carbonAvoidance.energySaved')}
                  icon={ESG_ICONS.energySavings}
                  value={fmt.metric('kwh', summary.total_energy_saved_kwh ?? 0).text}
                  unit={t('units.kwh')}
                />
                {show('completed_runs') && (
                  <KpiFigure
                    label={t('carbonAvoidance.completedRuns')}
                    icon={ESG_ICONS.runsCompleted}
                    value={fmt.metric('count', summary.completed_run_count).text}
                  />
                )}
                {show('skipped_runs') && (
                  <KpiFigure
                    label={t('carbonAvoidance.notCountedRuns')}
                    icon={ESG_ICONS.runsExcluded}
                    value={fmt.metric('count', excluded).text}
                    info={excluded > 0 ? t('carbonAvoidance.notCountedTooltip', { skipped, failed }) : undefined}
                  />
                )}
              </MetricStrip>
            )}
          </Box>
        </EsgSection>
      )}

      {showBreakdown && (
        <EsgSection
          title={t('carbonAvoidance.byStreamTitle')}
          icon={ESG_ICONS.materials}
          description={t('esgReports.definitions.carbonSubtitle')}
        >
          <Box sx={{ display: 'grid', gap: { xs: 2, md: 2.5 } }}>
            {(show('stream_chart') || show('stream_table')) && (
              <Box
                sx={{
                  display: 'grid',
                  gap: { xs: 2, md: 2.5 },
                  alignItems: 'stretch',
                  gridTemplateColumns: {
                    xs: 'minmax(0, 1fr)',
                    md:
                      show('stream_chart') && show('stream_table')
                        ? 'minmax(0, 5fr) minmax(0, 7fr)'
                        : 'minmax(0, 1fr)',
                  },
                }}
              >
                {show('stream_chart') && (
                  <CarbonAvoidedByStreamChart
                    streams={chartStreams}
                    title={t('carbonAvoidance.byStreamTitle')}
                    caption={t('units.tco2e')}
                    chartType="pie"
                    hideMenu
                  />
                )}
                {show('stream_table') && (
                  <CarbonAvoidanceByStreamTable rows={report.by_stream} caption={periodLabel} fillHeight />
                )}
              </Box>
            )}
            {show('material_table') && <CarbonAvoidedByMaterialTable rows={report.by_material} />}
          </Box>
        </EsgSection>
      )}
    </Box>
  );
};

export default CarbonAvoidanceDetailView;
